import { screens } from "../content/sitemap";

/**
 * Canonical `page_viewed` screen names — display values match
 * `buxus-frontend/src/modules/constants/constants.string.js`.
 * `screenConstant` is the stable export key; `screen` is the human label used in dashboards.
 */
export const AnalyticsScreenConstants = {
  AR_INTRO_SCREEN: "AR Responder Scenarios",
  AR_TRAINING_SCREEN: "AR Training",
  ASSET_LINE_MARKERS_SCREEN: "Asset Line Markers",
  ASSET_OVERVIEW_SCREEN: "Asset Overview",
  ASSET_PROFILE_FACILITY_SCREEN: "Facility Profile",
  ASSET_PROFILE_SCREEN: "Asset Profile",
  ASSET_SEARCH_RESULTS_SCREEN: "Assets",
  ASSET_SEARCH_SCREEN: "Asset Search",
  COMMUNICATIONS_SCREEN: "Radio Communications",
  COMMUNITY_PREPAREDNESS_SCREEN: "Community Preparedness",
  COMPANY_CONTACTS_LISTING_SCREEN: "Contacts",
  COMPANY_CONTACTS_SCREEN: "Contact Areas",
  COMPANY_INFORMATION_SCREEN: "Company Overview",
  COMPANY_RESPONSE_SCREEN: "Company Response",
  CONSTRUCTION_SCREEN: "Construction",
  CONTACT_SCREEN: "Contact",
  DAMAGE_PREVENTION_SCREEN: "Damage Prevention",
  DOCUMENTS_SCREEN: "Documents & Plans",
  DOWNLOAD_SCREEN: "Download",
  ENCROACHMENT_SCREEN: "Encroachment",
  FIRE_SUPPRESSION_SCREEN: "Fire Suppression",
  GENERAL_HAZARDS_SCREEN: "General Hazards",
  GENERAL_HAZARD_SCREEN: "General Hazard",
  HAZARDS_LIST_SCREEN: "Products & Hazards",
  HAZARDS_SCREEN: "Hazard",
  IMPACT_ZONES_SCREEN: "Impact Zones",
  IN_AN_EMERGENCY_ASSETS_SCREEN: "In an Emergency",
  IN_AN_EMERGENCY_SCREEN: "Emergency Response",
  INFO_SCREEN: "Information",
  INTEGRITY_MANAGEMENT_SCREEN: "Integrity Management",
  KEY_LOCATIONS_SCREEN: "Key Locations",
  LEAK_RECOGNITION_SCREEN: "Leak Recognition",
  LMS_TRAINING_SCREEN: "Pipeline Playbooks",
  LOGIN_SCREEN: "Login",
  MAINTENANCE_SCREEN: "Maintenance",
  MAKE_A_REQUEST_SCREEN: "Make a Request",
  ONE_CALL_SCREEN: "One Call",
  OPERATOR_LINE_MARKERS_SCREEN: "Line Markers",
  OPERATOR_OVERVIEW_SCREEN: "Operator Overview",
  OPERATOR_SEARCH_FORM_SCREEN: "Home",
  OPERATORS_SCREEN: "Operators",
  PREVENTION_SCREEN: "Prevention",
  PROFILE_SCREEN: "Profile",
  RELIABILITY_SCREEN: "Reliability",
  REPORT_AN_EMERGENCY_SCREEN: "Report an Emergency",
  REPORT_DAMAGE_SCREEN: "Report Damage",
  REQUEST_FORM_SCREEN: "Request Form",
  RESOURCE_SCREEN: "Resource",
  RESOURCES_SCREEN: "Resources",
  RESPONDER_ACTIONS_SCREEN: "Responder Actions",
  SAFETY_MEASURES_SCREEN: "Our Safety Measures",
  SAFETY_OVERVIEW_SCREEN: "Safety Overview",
  SAFETY_RESPONSIBILITIES_SCREEN: "Your Safety & Responsibilities",
  SAFETY_SCREEN: "Safety",
  SCENARIOS_SCREEN: "Scenarios",
  SEARCH_RESULTS_SCREEN: "Operator Search Results",
  SECURITY_SCREEN: "Security",
  SEE_SOMETHING_SAY_SOMETHING_SCREEN: "See Something, Say Something",
  SEND_ALERT_SCREEN: "Send Alert",
  SPECIAL_NOTE_SCREEN: "Special Note",
  SPECIAL_NOTES_SCREEN: "Special Notes",
  STEPS_TO_TAKE_SCREEN: "Steps to Take",
  SUSTAINABILITY_SCREEN: "Stewardship & Sustainability",
  TEST_YOUR_KNOWLEDGE_SCREEN: "Test Your Knowledge",
  TRAINING_DOCUMENTS_SCREEN: "Scenarios",
  TRAINING_SCENARIO_DETAIL_SCREEN: "Scenario",
  TOOLBOX_TIPS_SCREEN: "Toolbox Tips",
  TOOLBOX_TIP_DETAIL_SCREEN: "Toolbox Tip",
  TRAINING_SCREEN: "Training Portal",
  UNIQUE_ATTRIBUTE_SCREEN: "Unique Attribute",
  UNIQUE_ATTRIBUTES_SCREEN: "Unique Attributes",
  WEB_INVITE_SCREEN: "Invite",
  WEB_REGISTRATION_SCREEN: "Register",
  WORST_CASE_SCENARIOS_SCREEN: "Worst Case Scenarios",
} as const;

export type AnalyticsScreenConstantKey = keyof typeof AnalyticsScreenConstants;

export type PageViewedScreenProps = {
  /** Human-readable label — matches mobile `page_viewed` `screen` values. */
  screen: string;
  /** Stable key — matches mobile export names (e.g. `TRAINING_SCREEN`). */
  screenConstant: AnalyticsScreenConstantKey | string;
};

function propsForConstant(key: AnalyticsScreenConstantKey): PageViewedScreenProps {
  return { screenConstant: key, screen: AnalyticsScreenConstants[key] };
}

/** Internal web shell stack ids → mobile analytics constants. */
const WEB_SHELL_TO_CONSTANT: Record<string, AnalyticsScreenConstantKey> = {
  __download__: "DOWNLOAD_SCREEN",
  __home__: "OPERATOR_SEARCH_FORM_SCREEN",
  __operators__: "OPERATORS_SCREEN",
  __training__: "TRAINING_SCREEN",
  __training_lms__: "LMS_TRAINING_SCREEN",
  __training_scenarios__: "TRAINING_DOCUMENTS_SCREEN",
  __training_scenario_detail__: "TRAINING_SCENARIO_DETAIL_SCREEN",
  __training_toolbox_tips__: "TOOLBOX_TIPS_SCREEN",
  __training_toolbox_tip_detail__: "TOOLBOX_TIP_DETAIL_SCREEN",
  __alert__: "SEND_ALERT_SCREEN",
  __profile__: "PROFILE_SCREEN",
  __information__: "INFO_SCREEN",
  login: "LOGIN_SCREEN",
};

/** Shared content `screen.*` ids → mobile analytics constants. */
const CONTENT_SCREEN_ID_TO_CONSTANT: Record<string, AnalyticsScreenConstantKey> = {
  "screen.operatorOverview": "OPERATOR_OVERVIEW_SCREEN",
  "screen.inAnEmergency": "IN_AN_EMERGENCY_ASSETS_SCREEN",
  "screen.assets": "ASSET_SEARCH_RESULTS_SCREEN",
  "screen.assetOverview": "ASSET_OVERVIEW_SCREEN",
  "screen.assetProfile": "ASSET_PROFILE_SCREEN",
  "screen.assetEmergencyResponse": "IN_AN_EMERGENCY_SCREEN",
  "screen.assetEmergencyReport": "REPORT_AN_EMERGENCY_SCREEN",
  "screen.assetEmergencySteps": "STEPS_TO_TAKE_SCREEN",
  "screen.assetEmergencyResponder": "RESPONDER_ACTIONS_SCREEN",
  "screen.assetEmergencyCompany": "COMPANY_RESPONSE_SCREEN",
  "screen.assetEmergencyCommunications": "COMMUNICATIONS_SCREEN",
  "screen.assetEmergencyFireSuppression": "FIRE_SUPPRESSION_SCREEN",
  "screen.assetEmergencyWorstCase": "WORST_CASE_SCENARIOS_SCREEN",
  "screen.assetEmergencyKeyLocations": "KEY_LOCATIONS_SCREEN",
  "screen.assetLineMarkers": "ASSET_LINE_MARKERS_SCREEN",
  "screen.assetProductsHazards": "HAZARDS_LIST_SCREEN",
  "screen.assetHazard": "HAZARDS_SCREEN",
  "screen.assetDocuments": "DOCUMENTS_SCREEN",
  "screen.assetKeyLocations": "KEY_LOCATIONS_SCREEN",
  "screen.assetGeneralHazards": "GENERAL_HAZARDS_SCREEN",
  "screen.assetSpecialNotes": "SPECIAL_NOTES_SCREEN",
  "screen.companyOverview": "COMPANY_INFORMATION_SCREEN",
  "screen.contacts": "COMPANY_CONTACTS_LISTING_SCREEN",
  "screen.makeARequest": "MAKE_A_REQUEST_SCREEN",
  "screen.sustainability": "SUSTAINABILITY_SCREEN",
  "screen.testYourKnowledge": "TEST_YOUR_KNOWLEDGE_SCREEN",
  "screen.safetyOverview": "SAFETY_OVERVIEW_SCREEN",
  "screen.ourSafetyMeasures": "SAFETY_MEASURES_SCREEN",
  "screen.integrityManagement": "INTEGRITY_MANAGEMENT_SCREEN",
  "screen.reliability": "RELIABILITY_SCREEN",
  "screen.preventionMeasures": "PREVENTION_SCREEN",
  "screen.construction": "CONSTRUCTION_SCREEN",
  "screen.maintenance": "MAINTENANCE_SCREEN",
  "screen.security": "SECURITY_SCREEN",
  "screen.yourSafetyOverview": "SAFETY_RESPONSIBILITIES_SCREEN",
  "screen.lineMarkers": "OPERATOR_LINE_MARKERS_SCREEN",
  "screen.oneCall": "ONE_CALL_SCREEN",
  "screen.encroachment": "ENCROACHMENT_SCREEN",
  "screen.reportDamage": "REPORT_DAMAGE_SCREEN",
  "screen.seeSomething": "SEE_SOMETHING_SAY_SOMETHING_SCREEN",
  "screen.resources": "RESOURCES_SCREEN",
};

/**
 * Resolve `screen` + `screenConstant` for `page_viewed` from a web shell stack id or `screen.*` content id.
 *
 * Constants follow the **active shell tab** (`__home__` → Home, `__download__` → Download), not which
 * child component is mounted. Pre-download onboarding on `/home` still reports Home while the Home tab
 * is selected, even when it renders the download catalog UI.
 */
export function pageViewedScreenProps(screenId: string): PageViewedScreenProps {
  const shellKey = WEB_SHELL_TO_CONSTANT[screenId];
  if (shellKey) return propsForConstant(shellKey);

  const contentKey = CONTENT_SCREEN_ID_TO_CONSTANT[screenId];
  if (contentKey) return propsForConstant(contentKey);

  const def = screens[screenId];
  if (def?.title) {
    return { screenConstant: screenId, screen: def.title };
  }

  return { screenConstant: screenId, screen: screenId };
}
